import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Player } from '@lordicon/react';
import Layout from '../../Layout/Layout';
import { Container, Row, Col, Button } from 'reactstrap';
import TitleBar from '../../Layout/TitleBar';
import {
  useTranslator,
  useToast,
  useAppContext,
  withLoading,
  withMobileAppMode,
  cloudflareImage,
  callNativeMobileApp,
  getCountry,
} from '@front-activerse/common-components';
import CookbookDish from './CookbookDish';
import IngredientList from './CookbookIngredientList';
import Ingredient from './CookbookIngredient';
import CookbookNutritions from './CookbookNutritions';
import CookbookMealRating from './CookbookMealRating';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import { compareVersions } from 'compare-versions';
import { useOutletContext, useNavigate } from 'react-router-dom';
import FriscoButton from '../../MealPlan/MealView/components/FriscoButton';

interface ICookbookMealDetailsContainer {
  meal: any;
  addToFavorites: (mealId: number) => Promise<any>;
  removeFromFavorites: (mealId: number) => Promise<any>;
  mobileApp: any;
  rateMeal: (mealId: number, rating: number) => Promise<any>;
  refetch: () => void;
}

export const CookbookMealDetailsContainer: React.FC<ICookbookMealDetailsContainer> = ({
  meal,
  addToFavorites,
  removeFromFavorites,
  mobileApp,
  rateMeal,
  refetch,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess, dismiss } = useToast();
  const [visiblePoster, setVisiblePoster] = useState(true);
  const [multiplier, setMultiplier] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ratingRef = useRef<HTMLDivElement>(null);
  const playerRefBookmark = useRef<Player>(null);
  const playerRefInfo = useRef<Player>(null);

  const iconsIconFgColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-fg-color')
      .trim();
    return color || '#000000';
  }, []);

  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const [preloadInstacart, setPreloadInstacart] = useState(false);

  const navigate = useNavigate();

  const {
    consts: { INSTACART_FIREBASE_FUNCTION, INSTACART_URL, APP_BRAND, FRISCO },
    images: { playBtn, instacartLogo, plus, plusMulti, minus, minusMulti, star, addToTrackerIcon },
    lotties: { bookmarkIcon, bookmarkFullIcon, infoIcon },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  useEffect(() => {
    trackFirebaseEvent('screen_view', {
      firebase_screen: 'Meal_details',
      meal_id: meal.id,
      cookbook_meal_types: meal.tags.mealTypeTags.map((tag: any) => tag.name).join('/'),
      meals_tab: 'cookbook',
      meal_favourites: meal.isFavorite,
      selected_diet: meal.tags.dietTypeTags.map((tag: any) => tag.name).join('/'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMultiplier = (multiplier: number) => {
    setMultiplier(multiplier);
  };

  const renderMealVideo = () => {
    if (!meal.preparationVideoUrl) {
      return renderMealImage();
    }

    return (
      <div className="embed-responsive ratio ratio-1x1">
        <video
          ref={videoRef}
          src={meal.preparationVideoUrl}
          controls
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onEnded={() => setVisiblePoster(true)}
        />
        {visiblePoster ? (
          <div
            style={{
              backgroundImage: `url(${meal.preparationImageUrl
                .replace('(', '%28')
                .replace(')', '%29')})`,
            }}
            className="embed-responsive-item d-flex align-items-center justify-content-center"
            onClick={() => playVideo(videoRef)}
          >
            <img src={playBtn} alt="" />
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderMealImage = () => {
    if (meal.preparationImageUrl) {
      const coverImage = cloudflareImage(
        meal.preparationImageUrl,
        `${APP_BRAND}`,
        'cookbookMealDetails'
      );
      return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
    }
    return null;
  };

  const playVideo = async (myVideoRef: any) => {
    setVisiblePoster(false);

    try {
      await myVideoRef.current.play();
    } catch (err) {
      toastError();
      console.warn(err);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (meal.isFavorite) {
        const response = await removeFromFavorites(meal.id);
        playerRefBookmark.current?.playFromBeginning();
        if (response.data.cookbook.removeFavoriteMeal.code === 200) {
          toastSuccess(
            <p className="mb-0">
              {t('cookbook.favorites.notification.removedFromFav.content')}
              <Button
                color="link"
                onClick={() => {
                  addToFavorites(meal.id);
                  dismiss();
                }}
              >
                {t('meal.favorites.notification.removedFromFav.button')}
              </Button>
            </p>
          );
        }
      } else {
        const response = await addToFavorites(meal.id);
        playerRefBookmark.current?.playFromBeginning();
        if (response.data.cookbook.addFavoriteMeal.successCode === 200) {
          toastSuccess(
            <p className="mb-0">{t('cookbook.favorites.notification.addedToFav.content')}</p>
          );
        }
      }

      trackFirebaseEvent('user_action', {
        action: 'tap-add_favourites',
        location: 'Dietplan',
        meal_id: meal.id,
        cookbook_meal_types: meal.tags.mealTypeTags
          ? meal.tags.mealTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        selected_diet: meal.tags.dietTypeTags
          ? meal.tags.dietTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        meals_tab: 'cookbook',
        meal_favourites: meal.isFavorite,
      });
    } catch (err) {
      toastError();
    }
  };

  const handleInstaCart = async () => {
    const ingredients: any = [];

    meal.dishes.forEach((dish: any) => {
      dish.ingredients.forEach((ingredient: any) => {
        const measurement = ingredient.measurements[0];
        const measure = `${measurement.amount} ${measurement.text.replace('%s', '')}`;

        ingredients.push(`${measure} ${ingredient.name}`);
      });
    });

    let mealImg = '';

    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    }

    const request = {
      name: meal.name,
      ingredients,
      recipe: [],
      image: mealImg,
      description: `Cookbook recipe`,
    };

    setPreloadInstacart(true);

    const response = await fetch(INSTACART_FIREBASE_FUNCTION.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    response.json().then((data) => {
      setPreloadInstacart(false);

      if (data.instacartUrl) {
        const recipeData = encodeURIComponent(data.instacartUrl);
        const instacartUrl = `${INSTACART_URL}?recipeSourceUrl=${recipeData}&utm_source=instacart_growth_partnerships&aff_id=1818&offer_id=1&affiliate_platform=recipe_widget`;

        trackFirebaseEvent('user_action', {
          action: 'tap-instacart_button',
          location: 'Meal_details',
          meals_tab: 'cookbook',
        });

        if (
          mobileApp &&
          mobileApp.os &&
          mobileApp.os.toLowerCase().startsWith('ios') &&
          mobileApp.version &&
          compareVersions(mobileApp.version, '4.0.2')
        ) {
          callNativeMobileApp({
            action: 'deeplink_open',
            payload: {
              url: instacartUrl,
            },
          });
        } else {
          // workound for typescript error
          const win: Window = window;
          win.location = instacartUrl;
        }
      }
    });
  };

  const renderIngredients = (ingredients: any, dishId: number) =>
    ingredients.map((ingredient: any) => {
      return (
        <Ingredient
          key={ingredient.key}
          name={ingredient.name}
          measurements={ingredient.measurements}
          multiplier={multiplier}
          isReplaceable={ingredient.isReplaceable}
          replacements={ingredient.replacements}
          isReplaced={ingredient.isReplaced}
          originalIngredient={ingredient.originalIngredient}
          dishId={dishId}
          id={ingredient.id}
          mealId={meal.id}
          ingredientKey={ingredient.key}
        />
      );
    });

  const renderIngredientList = (ingredients: any, dishId: number) => {
    const filteredIngredients = ingredients.filter(
      (ingredient: any) => !ingredient.category.isSeasoning && !ingredient.category.isOptional
    );

    const onlySeasoningsIngredients = ingredients.filter(
      (ingredient: any) => ingredient.category.isSeasoning
    );

    const optionalIngredients = ingredients.filter(
      (ingredient: any) => ingredient.category.isOptional
    );

    return (
      <IngredientList
        renderIngredients={() => renderIngredients(filteredIngredients, dishId)}
        renderOnlySeasoningsIngredients={() => renderIngredients(onlySeasoningsIngredients, dishId)}
        renderOptionalIngredients={() => renderIngredients(optionalIngredients, dishId)}
      />
    );
  };

  const renderTags = () => {
    return (
      <div className="tags">
        {meal.tags.customTags &&
          meal.tags.customTags.map((tag: { id: number; labelColor: string; name: string }) => (
            <span
              className="tag me-1"
              key={tag.id}
              style={{ backgroundColor: tag.labelColor, color: '#fff' }}
            >
              {tag.name}
            </span>
          ))}
        {meal.tags.mealTypeTags &&
          meal.tags.mealTypeTags.map((tag: { id: number; name: string }) => (
            <span className="tag me-1" key={tag.id}>
              {tag.name}
            </span>
          ))}
        {meal.tags.dietTypeTags &&
          meal.tags.dietTypeTags.map((tag: { id: number; name: string }) => (
            <span className="tag me-1" key={tag.id}>
              {tag.name}
            </span>
          ))}
      </div>
    );
  };

  let showInstacartButton = false;
  if (getCountry() === 'Canada' || getCountry() === 'United States of America') {
    showInstacartButton = true;
  }

  const calculatePreparationTime = (minutes: number) => {
    const steps = [5, 15, 30, 45, 60];

    if (minutes === 0 || minutes == null) {
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step}`;
      }
    }
    return `> ${steps[steps.length - 1]}`;
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/cookbook/module/tracker/add-meal/${meal.id}`);
  };

  return (
    <Layout>
      <TitleBar level={3}>{t(`cookbook.recipeDetails.navbarTitle`)}</TitleBar>
      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="meal-details">
                  <Row>
                    <Col md="6">
                      <div className="meal-image-video">
                        {renderMealVideo()}
                        <Button
                          className="add-remove-fav"
                          color="link"
                          onClick={toggleFavorite}
                          onMouseEnter={() => playerRefBookmark.current?.playFromBeginning()}
                          onTouchStart={() => playerRefBookmark.current?.playFromBeginning()}
                        >
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              backgroundColor: 'rgba(0, 0, 0, 0.25)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                            }}
                          >
                            <Player
                              ref={playerRefBookmark}
                              icon={meal.isFavorite ? bookmarkFullIcon : bookmarkIcon}
                              size={22}
                              colorize={iconsIconFgColor}
                            />
                          </div>
                        </Button>
                        {visiblePoster && (
                          <div className="meal-info2 d-md-none">
                            <Container>
                              {renderTags()}
                              <h1 className="meal-title title-large semi-bold white mb-0">
                                {meal.name}
                              </h1>
                            </Container>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="instacart-container d-md-none">
                        {showInstacartButton && (
                          <div className="text-center">
                            <Button
                              color="instacart"
                              className="w-100 w-md-auto"
                              onClick={handleInstaCart}
                              disabled={preloadInstacart}
                            >
                              {preloadInstacart && (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              {t('orderIngredients.instacart.button')}{' '}
                              <img
                                src={instacartLogo}
                                style={{
                                  verticalAlign: 'baseline',
                                  height: '13px',
                                }}
                                alt="instacart"
                              />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="meal-info">
                        <div className="d-none d-md-block">
                          {renderTags()}
                          <h1 className="meal-title title-large semi-bold mb-xl">{meal.name}</h1>
                        </div>

                        <CookbookMealRating
                          rateMeal={rateMeal}
                          refetch={refetch}
                          meal={meal}
                          reference={ratingRef}
                        />

                        <div className="grey-box mb-2">
                          {t('mealDetails.info.prepTime.title')}{' '}
                          <strong className="float-end">
                            {calculatePreparationTime(meal.preparationTime.minutes)} min
                          </strong>
                        </div>

                        <div className="grey-box mb-3">
                          {t('mealDetails.info.servings.title')}{' '}
                          {meal.portionsAdjustable ? (
                            <strong className="float-end">
                              <button
                                className="servings-button me-3"
                                disabled={multiplier <= 1}
                                onClick={() => changeMultiplier(multiplier - 1)}
                              >
                                <img src={meal.portions === 1 ? minus : minusMulti} alt="" />
                              </button>
                              {meal.portions * multiplier}
                              <button
                                className="servings-button ms-3"
                                disabled={multiplier > 24}
                                onClick={() => changeMultiplier(multiplier + 1)}
                              >
                                <img src={meal.portions === 1 ? plus : plusMulti} alt="" />
                              </button>
                            </strong>
                          ) : (
                            <strong className="float-end">{meal.portions * multiplier}</strong>
                          )}
                        </div>

                        <Button
                          color="primary"
                          size="sm"
                          className="w-100 d-none d-md-block tracker"
                          onClick={handleAddMealToTracker}
                        >
                          <img
                            src={addToTrackerIcon}
                            alt={t('tracker.meal.addToTrackerButton')}
                            className="me-2"
                          />
                          {t('tracker.meal.addToTrackerButton')}
                        </Button>

                        <div className="d-none d-md-block">
                          <CookbookNutritions meal={meal} multiplier={multiplier} />
                        </div>

                        {showInstacartButton && (
                          <div className="text-center d-none d-md-block">
                            <Button
                              color="instacart"
                              className="w-100 w-md-auto"
                              onClick={handleInstaCart}
                              disabled={preloadInstacart}
                            >
                              {preloadInstacart && (
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                              )}
                              {t('orderIngredients.instacart.button')}{' '}
                              <img
                                src={instacartLogo}
                                style={{
                                  verticalAlign: 'baseline',
                                  height: '13px',
                                }}
                                alt="instacart"
                              />
                            </Button>
                          </div>
                        )}

                        {FRISCO && <FriscoButton meal={meal} publicName={meal.name} />}
                      </div>
                    </Col>
                  </Row>

                  {meal.dishes.map((dish: any, idx: number) => (
                    <div key={dish.id}>
                      <CookbookDish {...dish}>
                        {renderIngredientList(dish.ingredients, dish.id)}
                      </CookbookDish>
                      {idx < meal.dishes.length - 1 && <hr key={idx} className="d-md-none" />}
                    </div>
                  ))}

                  {meal.recipeNote && (
                    <div className="smart-tip-container">
                      <div className="smart-tip">
                        <p className="semi-bold text-text-main-color">
                          <img src={star} style={{ width: '24px' }} alt="" className="me-2" />
                          {t('mealDetails.smartTipBox.title')}
                        </p>
                        <p className="mb-0 small">{meal.recipeNote}</p>
                      </div>
                    </div>
                  )}

                  <div className="d-md-none">
                    <CookbookNutritions meal={meal} multiplier={multiplier} />
                  </div>

                  <div
                    className="smart-tip-container pt-0 pt-md-4 pb-5 pb-md-4"
                    onMouseEnter={() => playerRefInfo.current?.playFromBeginning()}
                    onTouchStart={() => playerRefInfo.current?.playFromBeginning()}
                  >
                    <div className="ingredients-info d-flex py-2">
                      <Player
                        ref={playerRefInfo}
                        icon={infoIcon}
                        size={24}
                        colorize={iconsIconMiddleColor}
                      />
                      <p className="mb-0 small ms-1">
                        {t('app.mealDetails.ingredientWeightInfo.content')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <footer className="d-md-none">
          <Container className="px-3">
            <Row>
              <Col>
                <Button
                  color="primary"
                  size="sm"
                  className="w-100 px-3 tracker"
                  onClick={handleAddMealToTracker}
                >
                  <img
                    src={addToTrackerIcon}
                    alt={t('tracker.meal.addToTrackerButton')}
                    className="me-2"
                  />
                  {t('tracker.meal.addToTrackerButton')}
                </Button>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(withMobileAppMode(CookbookMealDetailsContainer), Placeholder);
